import React from 'react';
import { Link as RouterLink, useLocation, useNavigate } from 'react-router-dom';
import { Box, Tab, Tabs } from '@mui/material';
import { alpha } from '@mui/material/styles';
import DashboardOutlinedIcon from '@mui/icons-material/DashboardOutlined';
import FormatListBulletedIcon from '@mui/icons-material/FormatListBulleted';
import HowToRegOutlinedIcon from '@mui/icons-material/HowToRegOutlined';
import useMobileView from '../../hooks/useMobileView';
import { detectThemeSync } from '../../utils/themeUtils';
import { PERMISSIONS } from '../../auth/permissions';
import usePermission from '../../auth/usePermission';

const ACCENT = '#4ce1b6';
const SELECTED_TAB_TEXT = '#042018';

export const BOOKING_SECTION_TABS = [
  {
    url: '/booking/dashboard/',
    label: 'Booking Dashboard',
    shortLabel: 'Dashboard',
    icon: DashboardOutlinedIcon,
  },
  {
    url: '/booking/list/',
    label: 'Booking List',
    shortLabel: 'List',
    icon: FormatListBulletedIcon,
  },
  {
    url: '/booking/web-checkin/',
    label: 'Web Check-in',
    shortLabel: 'Check-in',
    icon: HowToRegOutlinedIcon,
    permissionCode: PERMISSIONS.WEB_CHECKIN_MANAGE,
  },
];

function toAbsoluteAppPath(url) {
  if (!url) return '/';
  return url.startsWith('/') ? url : `/${url}`;
}

function isTabActive(pathname, url) {
  const target = toAbsoluteAppPath(url);
  if (!target) return false;
  if (pathname === target) return true;
  if (target.endsWith('/') && pathname === target.slice(0, -1)) return true;
  if (!target.endsWith('/') && `${pathname}/` === target) return true;
  const prefix = target.endsWith('/') ? target : `${target}/`;
  return pathname.startsWith(prefix);
}

export default function BookingSectionTabs({ isDark: isDarkProp, inline = false, sx = {} }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { isCompact } = useMobileView();
  const { can } = usePermission();
  const isDark = isDarkProp ?? detectThemeSync() === 'dark';
  const visibleTabs = BOOKING_SECTION_TABS.filter((tab) => (
    !tab.permissionCode || can(tab.permissionCode)
  ));

  const activeIndex = visibleTabs.findIndex((tab) => isTabActive(location.pathname, tab.url));
  const value = activeIndex >= 0 ? activeIndex : 0;
  const tabMinHeight = inline ? 36 : 44;
  const isMobileInline = inline && isCompact;

  return (
    <Box
      sx={{
        width: isMobileInline || !inline ? '100%' : 'auto',
        flexShrink: 0,
        minWidth: 0,
        ...sx,
      }}
    >
      <Box
        sx={{
          display: isMobileInline ? 'flex' : 'inline-flex',
          width: isMobileInline || !inline ? '100%' : 'auto',
          maxWidth: '100%',
          p: '3px',
          borderRadius: inline ? '12px' : '16px',
          border: `1px solid ${alpha(ACCENT, isDark ? 0.24 : 0.2)}`,
          background: isDark
            ? alpha('#ffffff', 0.04)
            : alpha('#ffffff', 0.92),
          boxShadow: inline
            ? 'none'
            : isDark
              ? `0 8px 24px rgba(0,0,0,0.22)`
              : `0 6px 18px rgba(15,23,42,0.06)`,
        }}
      >
        <Tabs
          value={value}
          onChange={(_, index) => navigate(toAbsoluteAppPath(visibleTabs[index].url))}
          variant={isCompact ? 'fullWidth' : 'standard'}
          aria-label="Booking section navigation"
          TabIndicatorProps={{ style: { display: 'none' } }}
          sx={{
            minHeight: tabMinHeight,
            width: isMobileInline || !inline ? '100%' : 'auto',
            '& .MuiTabs-flexContainer': {
              gap: '3px',
              width: isCompact ? '100%' : 'auto',
            },
          }}
        >
          {visibleTabs.map((tab, index) => {
            const Icon = tab.icon;
            const selected = value === index;
            const tabPath = toAbsoluteAppPath(tab.url);
            return (
              <Tab
                key={tab.url}
                component={RouterLink}
                to={tabPath}
                disableRipple
                icon={<Icon sx={{ fontSize: inline ? 16 : 18 }} />}
                iconPosition="start"
                label={isCompact ? tab.shortLabel : tab.label}
                sx={{
                  minHeight: tabMinHeight,
                  minWidth: isCompact ? 0 : inline ? 118 : 148,
                  flex: isCompact ? '1 1 0' : '0 1 auto',
                  px: { xs: 0.85, sm: inline ? 1.35 : 1.75 },
                  py: 0.65,
                  borderRadius: inline ? '9px' : '12px',
                  textTransform: 'none',
                  fontWeight: 700,
                  fontSize: isMobileInline ? '0.75rem' : inline ? '0.78rem' : '0.84rem',
                  letterSpacing: 0.05,
                  color: isDark ? '#94a3b8' : '#64748b',
                  background: selected
                    ? 'linear-gradient(180deg, #58f2ce 0%, #44e8bf 100%)'
                    : 'transparent',
                  border: selected ? `1px solid ${alpha(ACCENT, 0.75)}` : '1px solid transparent',
                  boxShadow: selected && !inline ? '0 6px 16px rgba(36, 226, 178, 0.2)' : 'none',
                  transition: 'all 0.2s ease',
                  '& .MuiTab-iconWrapper': {
                    marginRight: 0.45,
                    color: 'inherit',
                  },
                  '&.Mui-selected': {
                    color: `${SELECTED_TAB_TEXT} !important`,
                    fontWeight: 800,
                  },
                  '&.Mui-selected .MuiTab-iconWrapper': {
                    color: `${SELECTED_TAB_TEXT} !important`,
                  },
                  '&:hover': {
                    color: selected ? SELECTED_TAB_TEXT : ACCENT,
                    background: selected
                      ? 'linear-gradient(180deg, #6cf8d7 0%, #52ecc7 100%)'
                      : alpha(ACCENT, isDark ? 0.1 : 0.08),
                  },
                  '&.Mui-selected:hover': {
                    color: `${SELECTED_TAB_TEXT} !important`,
                  },
                }}
              />
            );
          })}
        </Tabs>
      </Box>
    </Box>
  );
}
